import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate, formatInteger, formatMoney } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type CouponType = "percentage" | "fixed" | "final_price";

type Coupon = {
  id: string;
  code: string;
  description: string | null;
  scope: "shop" | "retreats" | "both";
  retreatId: string | null;
  appliesToTierType: "any" | "deposit" | "full" | "addon";
  type: CouponType;
  value: number;
  minPurchaseCents: number | null;
  maxUses: number | null;
  currentUses: number;
  expiresAt: string | null;
  status: "active" | "disabled";
};

type RetreatOption = { id: string; title: string; subtitle: string | null };

type CouponSortKey = "code" | "type" | "value" | "scope" | "uses" | "status" | "expires";

type FormState = {
  code: string;
  description: string;
  scope: "shop" | "retreats" | "both";
  retreatId: string;
  appliesToTierType: "any" | "deposit" | "full" | "addon";
  type: CouponType;
  valueDisplay: string; // dollars for fixed/final price, percent integer for percentage
  minPurchaseDollars: string;
  maxUses: string;
  expiresAt: string;
  status: "active" | "disabled";
};

const empty: FormState = {
  code: "",
  description: "",
  scope: "shop",
  retreatId: "",
  appliesToTierType: "any",
  type: "percentage",
  valueDisplay: "",
  minPurchaseDollars: "",
  maxUses: "",
  expiresAt: "",
  status: "active",
};

function toForm(c: Coupon): FormState {
  return {
    code: c.code,
    description: c.description ?? "",
    scope: c.scope,
    retreatId: c.retreatId ?? "",
    appliesToTierType: c.appliesToTierType,
    type: c.type,
    valueDisplay: c.type === "percentage" ? String(c.value) : (c.value / 100).toFixed(2),
    minPurchaseDollars: c.minPurchaseCents != null ? (c.minPurchaseCents / 100).toFixed(2) : "",
    maxUses: c.maxUses != null ? String(c.maxUses) : "",
    expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : "",
    status: c.status,
  };
}

function formToPayload(form: FormState) {
  const value =
    form.type === "percentage"
      ? parseInt(form.valueDisplay || "0", 10)
      : Math.round(parseFloat(form.valueDisplay || "0") * 100);
  return {
    code: form.code,
    description: form.description || null,
    scope: form.scope,
    retreatId: form.scope === "retreats" ? form.retreatId || null : null,
    appliesToTierType: form.appliesToTierType,
    type: form.type,
    value,
    minPurchaseCents: form.minPurchaseDollars
      ? Math.round(parseFloat(form.minPurchaseDollars) * 100)
      : null,
    maxUses: form.maxUses ? parseInt(form.maxUses, 10) : null,
    expiresAt: form.expiresAt || null,
    status: form.status,
  };
}

function couponValue(coupon: Coupon) {
  if (coupon.type === "percentage") return `${coupon.value}%`;
  if (coupon.type === "final_price") return `Final ${formatMoney(coupon.value)}`;
  return formatMoney(coupon.value);
}

function couponSearchText(coupon: Coupon) {
  return [
    coupon.code,
    coupon.description,
    coupon.scope,
    coupon.appliesToTierType,
    coupon.type,
    couponValue(coupon),
    coupon.status,
    coupon.expiresAt ? formatDate(coupon.expiresAt) : "",
  ]
    .filter(Boolean)
    .join(" ");
}

const couponSorters: Record<CouponSortKey, (row: Coupon) => string | number> = {
  code: (row) => row.code,
  type: (row) => row.type,
  value: (row) => row.value,
  scope: (row) => row.scope,
  uses: (row) => row.currentUses,
  status: (row) => row.status,
  expires: (row) => dateSortValue(row.expiresAt),
};

export default function AdminCoupons() {
  const [list, setList] = useState<Coupon[]>([]);
  const [retreats, setRetreats] = useState<RetreatOption[]>([]);
  const [editing, setEditing] = useState<Coupon | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ coupons: Coupon[]; retreats: RetreatOption[] }>("/api/admin/coupons");
    setList(r.coupons);
    setRetreats(r.retreats);
  }

  useEffect(() => {
    refresh().finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm({ ...empty });
    setEditing("new");
    setErr(null);
  }

  function startEdit(c: Coupon) {
    setForm(toForm(c));
    setEditing(c);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = formToPayload(form);
      if (editing === "new") await api.post("/api/admin/coupons", payload);
      else if (editing) await api.put(`/api/admin/coupons/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Coupon) {
    if (!confirm(`Delete coupon "${c.code}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/coupons/${c.id}`);
    await refresh();
  }

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const filters = useMemo(
    () => [
      (row: Coupon) => statusFilter === "all" || row.status === statusFilter,
      (row: Coupon) => scopeFilter === "all" || row.scope === scopeFilter,
    ],
    [scopeFilter, statusFilter],
  );
  const retreatById = useMemo(
    () => new Map(retreats.map((retreat) => [retreat.id, retreat])),
    [retreats],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Coupon, CouponSortKey>({
    rows: list,
    search,
    initialSort: { key: "code", direction: "asc" },
    sorters: couponSorters,
    searchText: couponSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Coupons</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New coupon
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New coupon" : editing.code}
            eyebrow={editing === "new" ? "Create coupon" : "Edit coupon"}
            backLabel="Back to coupons"
            onBack={() => setEditing(null)}
          />

          <div className="grid2">
            <Field
              label="Code (auto-uppercased)"
              value={form.code}
              onChange={(v) => set("code", v)}
              required
            />
            <Field
              label="Internal description"
              value={form.description}
              onChange={(v) => set("description", v)}
            />
          </div>

          <div className="grid2">
            <label className="fld">
              <span>Discount type</span>
              <select
                value={form.type}
                onChange={(e) => {
                  const type = e.target.value as CouponType;
                  setForm((form) => ({
                    ...form,
                    type,
                    valueDisplay: "",
                    ...(type === "final_price"
                      ? { scope: "retreats" as const, appliesToTierType: "full" as const }
                      : {}),
                  }));
                }}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed amount</option>
                <option value="final_price">Final price</option>
              </select>
            </label>
            <Field
              label={
                form.type === "percentage"
                  ? "Value (percent, e.g. 10)"
                  : form.type === "final_price"
                    ? "Final price (USD)"
                    : "Value (USD)"
              }
              value={form.valueDisplay}
              onChange={(v) => set("valueDisplay", v)}
              required
            />
          </div>

          <div className="grid2">
            <label className="fld">
              <span>Scope</span>
              <select
                value={form.scope}
                onChange={(e) => {
                  const scope = e.target.value as FormState["scope"];
                  setForm((form) => ({
                    ...form,
                    scope,
                    ...(scope === "retreats" ? {} : { retreatId: "" }),
                  }));
                }}
              >
                <option value="shop" disabled={form.type === "final_price"}>Shop only</option>
                <option value="retreats">Retreats only</option>
                <option value="both" disabled={form.type === "final_price"}>Both</option>
              </select>
            </label>
            <label className="fld">
              <span>Target retreat</span>
              <select
                value={form.retreatId}
                disabled={form.scope !== "retreats"}
                onChange={(e) => set("retreatId", e.target.value)}
              >
                <option value="">All retreats</option>
                {retreats.map((retreat) => (
                  <option key={retreat.id} value={retreat.id}>
                    {retreat.title}{retreat.subtitle ? ` — ${retreat.subtitle}` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid2">
            <label className="fld">
              <span>Retreat tier filter</span>
              <select
                value={form.appliesToTierType}
                onChange={(e) =>
                  set("appliesToTierType", e.target.value as FormState["appliesToTierType"])
                }
              >
                <option value="any">Any</option>
                <option value="deposit">Deposit</option>
                <option value="full">Full</option>
                <option value="addon">Add-on</option>
              </select>
            </label>
          </div>

          <div className="grid2">
            <Field
              label="Min purchase (USD, optional)"
              value={form.minPurchaseDollars}
              onChange={(v) => set("minPurchaseDollars", v)}
            />
            <Field
              label="Max uses (blank = unlimited)"
              value={form.maxUses}
              onChange={(v) => set("maxUses", v)}
            />
          </div>

          <div className="grid2">
            <label className="fld">
              <span>Expires on</span>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => set("expiresAt", e.target.value)}
              />
            </label>
            <label className="fld">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value as FormState["status"])}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          </div>

          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search coupons, descriptions, scopes..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
            <AdminTableFilterSelect
              label="Scope"
              value={scopeFilter}
              onChange={setScopeFilter}
              options={[
                { value: "all", label: "All scopes" },
                { value: "shop", label: "Shop" },
                { value: "retreats", label: "Retreats" },
                { value: "both", label: "Both" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No coupons yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No coupons match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Code" sortKey="code" sort={sort} onSort={requestSort} />
                  <SortableTh label="Type" sortKey="type" sort={sort} onSort={requestSort} />
                  <SortableTh label="Value" sortKey="value" sort={sort} onSort={requestSort} />
                  <SortableTh label="Scope" sortKey="scope" sort={sort} onSort={requestSort} />
                  <SortableTh label="Uses" sortKey="uses" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <SortableTh label="Expires" sortKey="expires" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.code}</strong>
                      {c.description && <span className="slug">{c.description}</span>}
                    </td>
                    <td>{c.type === "final_price" ? "final price" : c.type}</td>
                    <td>{couponValue(c)}</td>
                    <td>
                      {c.scope}
                      {c.retreatId && (
                        <span className="slug">
                          {retreatById.get(c.retreatId)?.subtitle ??
                            retreatById.get(c.retreatId)?.title ??
                            "Targeted retreat"}
                        </span>
                      )}
                    </td>
                    <td>
                      {formatInteger(c.currentUses)}/{c.maxUses == null ? "unlimited" : formatInteger(c.maxUses)}
                    </td>
                    <td>{c.status}</td>
                    <td>{c.expiresAt ? formatDate(c.expiresAt) : "-"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(c)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(c)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld input[type=date], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
`;
